// Replays the local guest cart into the authenticated server cart, then
// clears the guest store. Idempotent: if the guest cart is empty, no-ops.
//
// Failures are non-fatal — we keep the guest cart intact and let the user
// retry later from the cart page.
import { useQueryClient } from '@tanstack/react-query';
import * as cartSvc from '@core/services/cartService';
import { useGuestCartStore } from '@core/store/guestCartStore';

export function useCartMerge() {
  const queryClient = useQueryClient();

  return async function mergeGuestCart() {
    const { lines, clear, ensureSession } = useGuestCartStore.getState();
    if (!lines.length) return { merged: 0, failed: 0 };

    const sessionId = ensureSession();
    let merged = 0;
    let failed = 0;
    for (const line of lines) {
      try {
        await cartSvc.addItem({
          itemId: line.itemId,
          quantity: line.quantity,
          variant: line.variant,
          modifiers: line.modifiers,
          selectedVariants: line.selectedVariants,
          selectedModifiers: line.selectedModifiers,
          uom: line.uom,
          selectedUomId: line.selectedUomId,
          conversionRate: line.conversionRate,
          alternativeUnit: line.alternativeUnit,
          alternativeUnitConversion: line.alternativeUnitConversion ?? line.alternativeUnitDetails,
          comboSelections: line.comboSelections,
          selectedComboItems: line.selectedComboItems,
          removedIngredients: line.removedIngredients,
          serviceItems: line.serviceItems,
          itemVariantSkuId: line.itemVariantSkuId,
          skuLocationId: line.skuLocationId,
          sessionId,
        });
        merged += 1;
      } catch {
        failed += 1;
      }
    }

    if (failed === 0) {
      clear();
    }
    queryClient.invalidateQueries({ queryKey: ['cart', 'active'] });
    return { merged, failed };
  };
}
